export enum KtznProject {
  /**
   *
   * Kotozna `fe-staff` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-staff
   */
  Staff = "fe-staff",
  /**
   * Kotozna `fe-guest` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-guest
   */
  Guest = "fe-guest",
  /**
   * Kotozna `fe-pwa` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-pwa
   */
  Pwa = "fe-pwa",
  /**
   * Kotozna `fe-admin` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-admin
   */
  Admin = "fe-admin",
  /**
   * Kotozna `fe-agent` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-agent
   */
  Agent = "fe-agent",
  /**
   * Kotozna `fe-shop` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-shop
   */
  Shop = "fe-shop",
  /**
   * Kotozna `fe-livechat-guest` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-livechat-guest
   */
  LiveChat = "fe-livechat-guest",
  /**
   * Kotozna `fe-admin-panel` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-fe-admin-panel
   */
  LamondoAdmin = "fe-admin-panel",
  /**
   * Kotozna `fe-graphql-compare` Vue web-app.
   *
   * Source: https://bitbucket.org/kotozna/ktzn-chat-fe-graphql-compare
   */
  GraphqlCompare = "fe-graphql-compare",
}

export interface IProjectService {
  /**
   * Gets the current frontend project from the .env file.
   * For example: `fe-pwa`, `fe-guest`, `fe-staff`, `fe-admin`, `fe-agent`...
   *
   * @returns Current KtznProject or null
   */
  getCurrent(): KtznProject | null;

  /**
   * Gets whether the current device is a mobile device or not
   * largely from "Mobile device detection" at developer.mozilla.org
   *
   * @returns boolean value for isMobile or not
   */
  getIsMobile(): boolean;

  /**
   * Returns the current project domain
   * Based on the following document: https://docs.google.com/spreadsheets/d/1p2nk9STzXv3107CaAPsSIoJKDXZktQ03rzpkyj1wYvk/edit#gid=0
   *
   * @param app Defaults to the current running project
   * @param region AWS deploy region (default: `jp`)
   * @param env Kotozna environment
   * @returns Kotozna domain as string or null
   */
  getDomain(app: string | undefined, region: string, env: string): string | null;
}

/**
 * Contains some project related variables
 */
export class ProjectService implements IProjectService {
  private _vueAppName: string | undefined;
  private _vueEnvName: string | undefined;

  constructor(vueAppName = process.env.VUE_APP_NAME, vueEnvName = process.env.VUE_APP_ENV_NAME) {
    this._vueAppName = vueAppName;
    this._vueEnvName = vueEnvName;
  }

  /**
   * Gets the current KtznProject from the .env file and returns `null` if not found.
   * For example: `fe-pwa`, `fe-guest`, `fe-staff`, `fe-admin`, `fe-agent`...
   *
   * @returns Current KtznProject or null
   */
  getCurrent = (): KtznProject | null => {
    console.log("VUE_APP_NAME: ", this._vueAppName);
    switch (this._vueAppName) {
      case "fe-staff":
        return KtznProject.Staff;
      case "fe-guest":
        return KtznProject.Guest;
      case "fe-pwa":
        return KtznProject.Pwa;
      case "fe-admin":
        return KtznProject.Admin;
      case "fe-agent":
        return KtznProject.Agent;
      case "fe-shop":
        return KtznProject.Shop;
      case "fe-livechat-guest":
        return KtznProject.LiveChat;
      case "fe-admin-panel":
        return KtznProject.LamondoAdmin;
      case "fe-graphql-compare":
        return KtznProject.GraphqlCompare;
      default:
        return null;
    }
  };

  /**
   * Gets whether the current device is a mobile device or not
   * largely from "Mobile device detection" at developer.mozilla.org
   *
   * @returns boolean value for isMobile or not
   */
  getIsMobile = (): boolean => {
    const userAgent = navigator.userAgent;
    if ("maxTouchPoints" in navigator) {
      return navigator.maxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia("(pointer:coarse)") && matchMedia("(pointer:coarse)");
      if (mQ && mQ.media === "(pointer:coarse)") {
        return mQ.matches;
      } else if ("orientation" in window) {
        return true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        return (
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(userAgent) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(userAgent)
        );
      }
    }
  };

  /**
   * Returns the current project domain
   * Based on the following document: https://docs.google.com/spreadsheets/d/1p2nk9STzXv3107CaAPsSIoJKDXZktQ03rzpkyj1wYvk/edit#gid=0
   *
   * @param app Defaults to the current running project
   * @param region AWS deploy region (default: `jp`)
   * @param env Kotozna environment
   * @returns Kotozna domain as string or null
   */
  getDomain = (
    app: string | undefined = this._vueAppName || undefined,
    region: string = "jp",
    env: string = this._vueEnvName || "prd"
  ): string | null => {
    switch (app) {
      case "fe-staff":
        // Legacy domain: staff.im.kotozna.chat
        return env == "prd"
          ? `https://staff.lm.${region}.im.kotozna.chat`
          : `https://staff.lm.${region}.${env}.im.kotozna.chat`;
      case "fe-guest":
        // Legacy domain: qc.kotozna.chat
        return env == "prd"
          ? `https://SUBDOMAIN.guest.ir.${region}.im.kotozna.chat`
          : `https://SUBDOMAIN.guest.ir.${region}.${env}.im.kotozna.chat`;
      case "fe-pwa":
        // Legacy domain: app.im.kotozna.chat
        return env == "prd"
          ? `https://staff.ir.${region}.im.kotozna.chat`
          : `https://staff.ir.${region}.${env}.im.kotozna.chat`;
      case "fe-admin":
        // Legacy domain: admin-console.im.kotozna.chat
        return env == "prd"
          ? `https://admin.kt.${region}.im.kotozna.chat`
          : `https://admin.kt.${region}.${env}.im.kotozna.chat`;
      case "fe-agent":
        // Legacy domain: agent-console.im.kotozna.chat
        return env == "prd"
          ? `https://agent.ir.${region}.im.kotozna.chat`
          : `https://agent.ir.${region}.${env}.im.kotozna.chat`;
      case "fe-shop":
        // Legacy domain: shop.im.kotozna.chat
        return env == "prd"
          ? `https://shop.${region}.im.kotozna.chat`
          : `https://shop.${region}.${env}.im.kotozna.chat`;
      case "fe-livechat-guest":
        // Legacy domain: lc-guest.im.kotozna.chat
        return env == "prd"
          ? `https://SUBDOMAIN.guest.lm.${region}.im.kotozna.chat`
          : `https://SUBDOMAIN.guest.lm.${region}.${env}.im.kotozna.chat`;
      case "fe-admin-panel":
        // Legacy domain: tenant-admin.im.kotozna.chat
        return env == "prd"
          ? `https://admin.lm.${region}.im.kotozna.chat`
          : `https://admin.lm.${region}.${env}.im.kotozna.chat`;
      case "fe-graphql-compare":
        // Project currently only exists in Staging
        return "https://stg-graphql-compare.im.kotozna.chat";
      default:
        return null;
    }
  };

  /**
   * Returns the origin given an url.
   * @param urlString (Optional) Input url as string. Default: `window.location.href`
   * @returns Origin as string
   * @Example
   * The input `https://stgv2.im.kotozna.chat/en/conversation/f4b0acd4` returns `https://stgv2.im.kotozna.chat`.
   */
  getOrigin = (urlString: string = window.location.href): string => {
    if (
      (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") &&
      urlString &&
      urlString.includes("localhost")
    ) {
      switch (this._vueAppName) {
        case "fe-staff":
          return "https://stg-staff.im.kotozna.chat";
        case "fe-guest":
          return process.env.VUE_APP_GUEST_ORIGIN || "";
        case "fe-pwa":
          return process.env.VUE_APP_KTZN_FE_DOMAIN || "";
        case "fe-admin":
          return process.env.VUE_APP_KTZN_FE_ADMIN_DOMAIN || "";
        case "fe-agent":
          return process.env.VUE_APP_KTZN_FE_AGENCY_DOMAIN || "";
        case "fe-shop":
          return process.env.VUE_APP_KTZN_FE_SHOP_DOMAIN || "";
        case "fe-livechat-guest":
          return process.env.VUE_APP_KTZN_FE_GUEST_DOMAIN || "";
        case "fe-admin-panel":
          return "https://stg-tenant-admin.im.kotozna.chat/";
        case "fe-graphql-compare":
          return "https://stg-graphql-compare.im.kotozna.chat";
        default:
          return "";
      }
    } else {
      return new URL(urlString).origin;
    }
  };
}
